/**
 * Consultant Detail Page
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Edit as EditSlotIcon,
  Delete as DeleteSlotIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Star as StarIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { PageHeader, ConfirmDialog } from '../../components/common';
import {
  useGetConsultantQuery,
  useDeleteConsultantMutation,
  useActivateConsultantMutation,
  useDeactivateConsultantMutation,
} from '../../store/api/consultantsApi';
import {
  useGetAvailabilitySlotsQuery,
  useCreateAvailabilitySlotMutation,
  useUpdateAvailabilitySlotMutation,
  useDeleteAvailabilitySlotMutation,
  AvailabilitySlot,
} from '../../store/api/availabilitySlotsApi';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../hooks/usePermissions';
import ProviderProfileDetails from '../../components/providers/ProviderProfileDetails';
import ProviderDocumentsSection from '../../components/providers/ProviderDocumentsSection';

const ConsultantDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManageConsultants = hasPermission('manage_consultants');

  const { data: consultantData, isLoading, error } = useGetConsultantQuery(Number(id!), {
    skip: !id,
  });

  const { data: slotsData, isLoading: slotsLoading } = useGetAvailabilitySlotsQuery(
    {
      consultant_id: Number(id!),
      page: 1,
      page_size: 100,
    },
    { skip: !id }
  );

  const [deleteConsultant, { isLoading: isDeleting }] = useDeleteConsultantMutation();
  const [activateConsultant] = useActivateConsultantMutation();
  const [deactivateConsultant] = useDeactivateConsultantMutation();
  const [createSlot, { isLoading: isCreatingSlot }] = useCreateAvailabilitySlotMutation();
  const [updateSlot, { isLoading: isUpdatingSlot }] = useUpdateAvailabilitySlotMutation();
  const [deleteSlot] = useDeleteAvailabilitySlotMutation();

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [slotDialog, setSlotDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState<AvailabilitySlot | null>(null);
  const [slotFormData, setSlotFormData] = useState({
    days: [] as string[],
    start_time: '09:00',
    end_time: '17:00',
  });

  const consultant = consultantData?.data;

  const daysOfWeek = [
    { value: 'monday', label: t('consultants.days.monday') },
    { value: 'tuesday', label: t('consultants.days.tuesday') },
    { value: 'wednesday', label: t('consultants.days.wednesday') },
    { value: 'thursday', label: t('consultants.days.thursday') },
    { value: 'friday', label: t('consultants.days.friday') },
    { value: 'saturday', label: t('consultants.days.saturday') },
    { value: 'sunday', label: t('consultants.days.sunday') },
  ];

  const handleDelete = async () => {
    if (!consultant) return;

    try {
      await deleteConsultant(consultant.id).unwrap();
      toast.success(t('consultants.toast.deleteSuccess'));
      navigate('/consultants');
    } catch (err: any) {
      toast.error(err?.data?.message || t('consultants.toast.deleteError'));
    }
  };

  const handleActivate = async () => {
    if (!consultant) return;

    try {
      await activateConsultant(consultant.id).unwrap();
      toast.success(t('consultants.toast.activateSuccess'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('consultants.toast.activateError'));
    }
  };

  const handleDeactivate = async () => {
    if (!consultant) return;

    try {
      await deactivateConsultant(consultant.id).unwrap();
      toast.success(t('consultants.toast.deactivateSuccess'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('consultants.toast.deactivateError'));
    }
  };

  const handleOpenSlotDialog = (slot?: AvailabilitySlot) => {
    if (slot) {
      setEditingSlot(slot);
      // Extract days from existing slots for this consultant
      const existingDays = slotsData?.results
        ?.filter((s) => s.consultant === consultant?.id && s.start_time === slot.start_time && s.end_time === slot.end_time)
        .map((s) => s.day_name.toLowerCase()) || [];
      setSlotFormData({
        days: existingDays,
        start_time: slot.start_time.substring(0, 5),
        end_time: slot.end_time.substring(0, 5),
      });
    } else {
      setEditingSlot(null);
      setSlotFormData({
        days: [],
        start_time: '09:00',
        end_time: '17:00',
      });
    }
    setSlotDialog(true);
  };

  const handleCloseSlotDialog = () => {
    setSlotDialog(false);
    setEditingSlot(null);
    setSlotFormData({
      days: [],
      start_time: '09:00',
      end_time: '17:00',
    });
  };

  const handleSlotSubmit = async () => {
    if (!consultant || slotFormData.days.length === 0) {
      toast.error(t('consultants.toast.selectDay'));
      return;
    }

    try {
      if (editingSlot) {
        // For editing, we need to update the slot
        await updateSlot({
          id: editingSlot.id,
          data: {
            consultant: consultant.id,
            days: slotFormData.days,
            start_time: slotFormData.start_time,
            end_time: slotFormData.end_time,
          },
        }).unwrap();
        toast.success(t('consultants.toast.slotUpdateSuccess'));
      } else {
        await createSlot({
          consultant: consultant.id,
          days: slotFormData.days,
          start_time: slotFormData.start_time,
          end_time: slotFormData.end_time,
        }).unwrap();
        toast.success(t('consultants.toast.slotCreateSuccess'));
      }
      handleCloseSlotDialog();
    } catch (err: any) {
      toast.error(err?.data?.message || t('consultants.toast.slotSaveError'));
    }
  };

  const handleDeleteSlot = async (slotId: number) => {
    try {
      await deleteSlot(slotId).unwrap();
      toast.success(t('consultants.toast.slotDeleteSuccess'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('consultants.toast.slotDeleteError'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !consultant) {
    const err = error as { status?: number; data?: { message?: string } } | undefined;
    const isForbidden = err?.status === 403;
    const message = isForbidden
      ? err?.data?.message || t('consultants.errors.forbidden')
      : t('consultants.errors.notFound');

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {message}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/consultants')}
          sx={{ mt: 2 }}
        >
          {t('consultants.buttons.backToList')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('consultants.detailTitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.consultants'), path: '/consultants' },
          { label: consultant.full_name },
        ]}
        action={
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {canManageConsultants && (
              <>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/consultants/${consultant.id}/edit`)}
                >
                  {t('common.edit')}
                </Button>
                {consultant.is_active ? (
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleDeactivate}
                  >
                    {t('consultants.buttons.deactivate')}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleActivate}
                  >
                    {t('consultants.buttons.activate')}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialog(true)}
                >
                  {t('common.delete')}
                </Button>
              </>
            )}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/consultants')}
            >
              {t('consultants.buttons.back')}
            </Button>
          </Box>
        }
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
        {/* Consultant Overview */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                src={consultant.profile_picture || undefined}
                alt={consultant.full_name}
                sx={{ width: 80, height: 80, mr: 3 }}
              >
                {consultant.full_name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {consultant.full_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                  {consultant.status && (
                    <Chip
                      label={t(`consultants.status.${consultant.status}`)}
                      size="small"
                      color={getStatusColor(consultant.status) as any}
                    />
                  )}
                  <Chip
                    icon={consultant.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                    label={consultant.is_active ? t('consultants.status.active') : t('consultants.status.inactive')}
                    size="small"
                    color={consultant.is_active ? 'success' : 'default'}
                  />
                  {consultant.is_verified && (
                    <Chip label={t('consultants.verified')} size="small" color="success" />
                  )}
                </Box>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            {/* Personal Information Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('consultants.form.sections.personalInfo')}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('consultants.form.email')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {consultant.email || '—'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('consultants.form.phoneNumber')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {consultant.phone_number}
                    </Typography>
                  </Box>
                </Grid>
                {consultant.age && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {t('consultants.form.age')}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={500}>
                        {t('consultants.detail.ageYears', { age: consultant.age })}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Professional Information Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <WorkIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('consultants.form.sections.professionalInfo')}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WorkIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('consultants.detail.specialization')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {consultant.specialization || '—'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <StarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('consultants.detail.rating')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      ⭐ {parseFloat(consultant.rating).toFixed(1)} {t('consultants.detail.reviewsCount', { count: consultant.total_reviews })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AssignmentIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('consultants.detail.totalConsultations')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {consultant.total_consultations}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('consultants.detail.completedConsultations')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {consultant.completed_consultations}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Metadata Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('consultants.detail.metadata')}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('consultants.detail.createdAt')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {moment(consultant.created_at).format('MMMM DD, YYYY HH:mm')}
                    </Typography>
                  </Box>
                </Grid>
                {consultant.approved_at && (
                  <>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircleIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {t('consultants.detail.approvedAt')}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={500}>
                        {moment(consultant.approved_at).format('MMMM DD, YYYY HH:mm')}
                      </Typography>
                    </Box>
                  </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </CardContent>
        </Card>

        {/* Contact preferences, coverage, clinic, usual working hours */}
        <ProviderProfileDetails provider={consultant} />

        {/* Availability Slots */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('consultants.slots.title')}
                </Typography>
              </Box>
              {canManageConsultants && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenSlotDialog()}
                >
                  {t('consultants.slots.addSlots')}
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />
            {slotsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : slotsData?.results && slotsData.results.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('consultants.slots.cols.date')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('consultants.slots.cols.day')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('consultants.slots.cols.time')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('consultants.slots.cols.duration')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('consultants.slots.cols.status')}</TableCell>
                      {canManageConsultants && (
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          {t('common.actions')}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slotsData.results.map((slot) => (
                      <TableRow key={slot.id}>
                        <TableCell>{moment(slot.date).format('MMM DD, YYYY')}</TableCell>
                        <TableCell>{slot.day_name}</TableCell>
                        <TableCell>
                          {moment(slot.start_time, 'HH:mm:ss').format('h:mm A')} - {moment(slot.end_time, 'HH:mm:ss').format('h:mm A')}
                        </TableCell>
                        <TableCell>{t('consultants.slots.minutes', { count: slot.slot_duration })}</TableCell>
                        <TableCell>
                          <Chip
                            label={slot.is_active ? t('consultants.status.active') : t('consultants.status.inactive')}
                            size="small"
                            color={slot.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        {canManageConsultants && (
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenSlotDialog(slot)}
                              color="primary"
                            >
                              <EditSlotIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSlot(slot.id)}
                              color="error"
                            >
                              <DeleteSlotIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('consultants.slots.empty')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <ProviderDocumentsSection
        kind="consultants"
        providerId={consultant.id}
        canManage={canManageConsultants}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('consultants.dialog.deleteTitle')}
        message={t('consultants.dialog.deleteMessage', { name: consultant.full_name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={isDeleting}
      />

      {/* Availability Slot Dialog */}
      <Dialog open={slotDialog} onClose={handleCloseSlotDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSlot ? t('consultants.slots.dialog.editTitle') : t('consultants.slots.dialog.addTitle')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('consultants.slots.dialog.days')}</InputLabel>
            <Select
              multiple
              value={slotFormData.days}
              onChange={(e) => setSlotFormData({ ...slotFormData, days: e.target.value as string[] })}
              label={t('consultants.slots.dialog.days')}
              renderValue={(selected) => (selected as string[]).map((day) => daysOfWeek.find((d) => d.value === day)?.label).join(', ')}
            >
              {daysOfWeek.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  <Checkbox checked={slotFormData.days.indexOf(day.value) > -1} />
                  {day.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="time"
            label={t('consultants.slots.dialog.startTime')}
            value={slotFormData.start_time}
            onChange={(e) => setSlotFormData({ ...slotFormData, start_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="time"
            label={t('consultants.slots.dialog.endTime')}
            value={slotFormData.end_time}
            onChange={(e) => setSlotFormData({ ...slotFormData, end_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSlotDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSlotSubmit}
            disabled={slotFormData.days.length === 0 || isCreatingSlot || isUpdatingSlot}
          >
            {isCreatingSlot || isUpdatingSlot ? t('consultants.buttons.saving') : editingSlot ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultantDetailPage;

